import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Tooltip, Tag } from 'antd';
import { ClockCircleOutlined, StopOutlined, EditOutlined } from '@ant-design/icons';
import cn from 'classnames';

import { finishPromotionActivity } from '@stores/activity';
import Image from '@components/Image';
import { getPath } from '@helpers/navigation';
import {
  allowEdit,
  promotionActivityIsActive,
  chooseStatusColor,
  chooseStatusText,
} from './PromotionActivityPost.helpers';
import { FULL_TIME_FORMAT } from '@config/format';
import { LOCALE_NAME } from '@stores/localeWeight';
import { IPromotionActivity } from '@interfaces/IPromotionActivity';
import { openModal } from '@stores/modals';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../PromotionActivityPage.content.json';
import css from './PromotionActivityPost.module.less';

interface PromotionActivityPostProps {
  activity: IPromotionActivity;
}

const PromotionActivityPost: FunctionComponent<PromotionActivityPostProps> = ({ activity }) => {
  const content = useTranslate(componentContent);

  function getListTag<T extends { [key: string]: string | number | any }>(
    list: T[],
    idKey: string,
    nameKey: string,
    color: string,
    size = 3,
  ): JSX.Element[] {
    const listTag =
      list &&
      list.slice(0, size).map((item) => (
        <Tag
          color={color}
          key={item[idKey]}
        >
          {item[nameKey]}
        </Tag>
      ));
    if (list && list.length > size) {
      const gameOver = list
        .slice(size)
        .map((item) => item[nameKey])
        .join(', ');

      listTag.push(
        <Tooltip
          placement='right'
          trigger='hover'
          title={gameOver}
          key='over'
        >
          <Tag color={color}>...</Tag>
        </Tooltip>,
      );
    }
    return listTag;
  }

  function getLocalesListTag<T extends { [key: string]: string | number | any }>(
    list: T[],
    color: string,
    size = 3,
  ): JSX.Element[] {
    const listTag =
      list &&
      list.slice(0, size).map(
        (item, index) =>
          item && (
            <Tag
              color={color}
              key={item && item[index]}
            >
              {item !== null ? item.toUpperCase() : null}
            </Tag>
          ),
      );
    if (list && list.length > size) {
      const gameOver = list
        .slice(size)
        .map((item) => {
          item !== null ? item.toUpperCase() : null;
        })
        .join(', ');

      listTag.push(
        <Tooltip
          placement='right'
          trigger='hover'
          title={gameOver}
          key='over'
        >
          <Tag color={color}>...</Tag>
        </Tooltip>,
      );
    }
    return listTag;
  }

  const timeBlock = useMemo(() => {
    const { starts, ends } = activity;
    return (
      <div className={css.metaLineItem}>
        <span className={css.metaIcon}>
          <ClockCircleOutlined />
        </span>
        <span>
          {`${moment(starts).format(FULL_TIME_FORMAT)} - ${
            ends ? moment(ends).format(FULL_TIME_FORMAT) : ''
          }`}
        </span>
      </div>
    );
  }, []);

  const games = useMemo(() => {
    const color = 'orange';
    if (activity.filter?.games !== null && activity.filter?.games.length !== 0) {
      return getListTag(activity.filter?.games, 'id', 'name', color);
    } else return null;
  }, [activity?.filter?.games]);

  const locales = useMemo(() => {
    const color = 'success';

    if (activity.filter?.locales !== null && activity.filter?.locales.length !== 0) {
      return getLocalesListTag(activity.filter?.locales, color);
    } else return null;
  }, [activity?.filter?.games]);

  const editPromotionActivity = useCallback(() => {
    openModal({ name: 'activity', data: activity });
  }, [activity]);

  const finishForcedPromotionActivity = useCallback(() => {
    finishPromotionActivity(activity);
  }, [activity]);

  const allowChangePromotionActivity = useMemo(() => {
    return allowEdit(activity.status);
  }, [activity.status]);

  const isActive = useMemo(() => {
    return promotionActivityIsActive(activity.status);
  }, [activity.status]);

  return (
    <div className={cn(css.root, isActive && css.notActive)}>
      <div className={css.image}>
        <Image
          className={css.image}
          url={activity.postDto.files?.[0]?.image?.url || ''}
        />
      </div>
      <div className={css.content}>
        <div className={css.header}>
          <Link
            to={getPath('postDetail', { postId: activity.postDto.id })}
            className='clickable'
          >
            {content.post_id} : {activity.postDto.id}
          </Link>
          <div className='clickable'>
            {content.title} : {activity.name}
          </div>
        </div>
        <div className={css.body}>
          {activity?.filter?.locales && (
            <div className={css.info}>
              <span className={css.title}>{content.locales}:</span>
              {locales}
            </div>
          )}

          {activity?.filter?.games && games && (
            <div className={css.info}>
              <span className={css.title}>{content.game}</span>
              {games}
            </div>
          )}

          <div className={css.info}>
            <span className={css.title}>{content.status}:</span>
            <Tag color={chooseStatusColor(activity.status)}>
              {chooseStatusText(activity.status, content)}
            </Tag>
          </div>
        </div>
        <div className={css.footer}>
          <div className={cn(css.metaLine, css.meta)}>
            {timeBlock}
            {isActive && (
              <div
                className={css.metaLineItem}
                onClick={finishForcedPromotionActivity}
              >
                <span className={css.metaIcon}>
                  <StopOutlined />
                </span>
                <span className='clickable'>{content.block}</span>
              </div>
            )}
            {allowChangePromotionActivity && (
              <div className={css.metaLineItem}>
                <span className={css.metaIcon}>
                  <EditOutlined />
                </span>
                <span
                  className='clickable'
                  onClick={editPromotionActivity}
                >
                  {content.editing}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionActivityPost;
